/** What a finished identifier lookup means for the value now in the field. */
export const IDENTIFIER_CHECK_OUTCOME = {
    RECHECK: 'recheck',
    DUPLICATE: 'duplicate',
    CLEAR: 'clear'
}

/**
 * The identifier as it is saved and looked up: whitespace removed, case kept.
 * @param {?string} identifier
 * @returns {string} an empty string for null
 */
export function normalisePatientIdentifier(identifier) {
    return (identifier || '').replace(/\s+/g, '')
}

// The API compares identifiers case-insensitively when deciding whether an update needs a duplicate check.
function comparablePatientIdentifier(identifier) {
    return normalisePatientIdentifier(identifier).toLowerCase()
}

/**
 * Whether an edited identifier differs from the one the patient was loaded with, on the API's terms:
 * ignoring whitespace and case. An unchanged identifier needs no duplicate check.
 * @param {?string} originalIdentifier
 * @param {?string} identifier
 * @returns {boolean}
 */
export function hasPatientIdentifierChanged(originalIdentifier, identifier) {
    return comparablePatientIdentifier(originalIdentifier) !== comparablePatientIdentifier(identifier)
}

/**
 * Whether a lookup result is a different patient from the one being edited.
 * @param {?{id: string}} foundPatient the lookup result, null when nobody has the identifier
 * @param {string} patientId the patient being edited
 * @returns {boolean}
 */
export function isIdentifierTakenByAnotherPatient(foundPatient, patientId) {
    return !!foundPatient && foundPatient.id !== patientId
}

/**
 * Decides what a finished lookup means for the field. The field stays editable during the lookup,
 * so a result for an earlier value must not decide the current one.
 * @param {string} checkedIdentifier the identifier that was looked up
 * @param {?string} currentIdentifier the identifier in the field now
 * @param {boolean} isDuplicate whether the lookup found a clash
 * @returns {string} one of IDENTIFIER_CHECK_OUTCOME
 */
export function identifierCheckOutcome(checkedIdentifier, currentIdentifier, isDuplicate) {
    if(normalisePatientIdentifier(checkedIdentifier) !== normalisePatientIdentifier(currentIdentifier)) return IDENTIFIER_CHECK_OUTCOME.RECHECK
    return isDuplicate ? IDENTIFIER_CHECK_OUTCOME.DUPLICATE : IDENTIFIER_CHECK_OUTCOME.CLEAR
}

function warnLookupFailed(identifier, error) {
    console.warn(`Patient identifier lookup failed for '${identifier}'; the API will check for a duplicate on save`, error)
}

/**
 * Creates a lookup shared by the identifier field's focusout and the save/create button, which fire
 * together when the button is clicked straight from the field. A lookup in flight or finished for the
 * same identifier is reused rather than repeated.
 *
 * A failed lookup resolves to null so the form carries on and the API remains the final guard; it is
 * not remembered, so the next call retries.
 * @param {function(string): Promise<?Object>} findPatientByIdentifier resolves to the patient, or null
 * @param {function(string, Error): void} [onLookupFailed] defaults to a console warning
 * @returns {function(string): Promise<?Object>} resolves to the patient holding the identifier, or null
 */
export function createPatientIdentifierCheck(findPatientByIdentifier, onLookupFailed = warnLookupFailed) {
    let lastCheck = null

    return function checkPatientIdentifier(identifier) {
        if(lastCheck && lastCheck.identifier === identifier) {
            return lastCheck.patient
        }

        const check = { identifier, patient: null }
        check.patient = Promise.resolve()
            .then(() => findPatientByIdentifier(identifier))
            .catch(error => {
                onLookupFailed(identifier, error)
                if(lastCheck === check) lastCheck = null
                return null
            })

        lastCheck = check
        return check.patient
    }
}
